import { useState } from "react";

import { FeedbackStatus, MyFeedbackRow, useMyFeedback } from "../../api/feedback";
import StatusBadge, { STATUS_LABEL } from "./StatusBadge";

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function fmtTime(iso: string): string {
  const dt = new Date(iso);
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function KindTag({ kind }: { kind: string }) {
  const tone =
    kind === "bug"
      ? "bg-rose-500/15 text-rose-300"
      : kind === "feature"
        ? "bg-violet-500/15 text-violet-300"
        : "bg-slate-800 text-slate-400";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${tone}`}>
      {kind}
    </span>
  );
}

function Card({ row }: { row: MyFeedbackRow }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <KindTag kind={row.kind} />
          <span className="truncate text-sm text-slate-100">{row.title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={row.status as FeedbackStatus} />
          <span className="text-[11px] text-slate-500">{relTime(row.created_at)}</span>
        </div>
      </button>

      {row.user_msg && (
        <p className="mt-2 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-300">
          <span className="font-medium text-slate-400">Note: </span>
          {row.user_msg}
        </p>
      )}

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-slate-800 pt-3 text-xs text-slate-300">
          <div>
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Details
            </div>
            <p className="whitespace-pre-wrap text-slate-300">{row.body}</p>
            {row.page_url && (
              <p className="mt-1 text-[11px] text-slate-500">
                Submitted from <span className="font-mono">{row.page_url}</span>
              </p>
            )}
          </div>
          {row.history.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                Status history
              </div>
              <ol className="space-y-1.5">
                {row.history.map((h, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="font-mono text-[10px] text-slate-500">
                      {fmtTime(h.created_at)}
                    </span>
                    <span className="text-slate-400">
                      {h.from_status
                        ? `${STATUS_LABEL[h.from_status as FeedbackStatus] ?? h.from_status} → `
                        : ""}
                      <span className="text-slate-200">
                        {STATUS_LABEL[h.to_status as FeedbackStatus] ?? h.to_status}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function MyFeedbackSection() {
  const q = useMyFeedback();

  return (
    <section className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <h2 className="mb-1 text-base font-semibold text-slate-100">Your feedback</h2>
      <p className="mb-4 text-sm text-slate-400">
        Bugs you&apos;ve reported and features you&apos;ve suggested. Click an item for details.
      </p>

      {q.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : q.isError ? (
        <div className="text-sm text-rose-300">Couldn&apos;t load. {q.error.message}</div>
      ) : !q.data || q.data.length === 0 ? (
        <div className="text-sm text-slate-500">
          No submissions yet. Click <span className="text-slate-300">Send feedback</span> in the
          header to report a bug or suggest a feature.
        </div>
      ) : (
        <ul className="space-y-2">
          {q.data.map((row) => (
            <Card key={row.id} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}
